import React from 'react';

import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowProcedureFlow from './components/WorkflowProcedureFlow';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  { label: 'structure/tech', title: 'structure/techルールの作成', active: true },
  {
    label: 'スキーマ+API仕様書',
    title: 'スキーマ定義とAPI仕様書の生成',
    path: '/devdocs/handson/schema-api',
  },
  {
    label: 'API実装ワークフロー',
    title: 'API実装ワークフローを作成する',
    path: '/devdocs/handson/api-workflow',
  },
  { label: 'E2Eテスト', title: 'E2Eテストを実行する', path: '/devdocs/handson/e2e' },
];

const HandsonStructureTech: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="実装編 — structure/techルールの作成"
      seriesName="実装編"
      tags={['Hands-on', 'Structure', 'Tech', 'Architecture']}
      seriesSteps={seriesSteps}
      prevLink={{ label: 'product作成', path: '/devdocs/handson/product' }}
      nextLink={{ label: 'スキーマ定義 + API仕様書の生成', path: '/devdocs/handson/schema-api' }}
    >
      <WorkflowGoalBox
        prerequisites={['「要件定義編 — product作成」が完了している']}
        completionCriteria={[
          '.kiro/steering/structure.md が作成されている',
          '.kiro/steering/tech.md が作成されている',
          '両ファイルとも inclusion: always で設定されている',
          'セクション5のチェックリスト全項目に合格している',
        ]}
      >
        実装フェーズの土台となるstructure.md（プロジェクト構成ルール）とtech.md（コーディング規約ルール）を自分で考えて作成する。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="要件定義編 — product作成"
        description="前の記事"
        path="/devdocs/handson/product"
      />
      <WorkflowLinkCard
        title="要件定義編 — Steeringルールの書き方"
        description="Steeringの基礎知識"
        path="/devdocs/handson/steering-rules"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'この章で体験すること' },
          { id: 'section-2', label: '実装方針を決める' },
          { id: 'section-3', label: 'structure.mdを作成する' },
          { id: 'section-4', label: 'tech.mdを作成する' },
          { id: 'section-5', label: 'チェックリストで品質を確認する' },
          { id: 'section-6', label: '現在のプロジェクト構成' },
          { id: 'section-7', label: 'ベストプラクティスとアンチパターン' },
          { id: 'section-8', label: 'この章で学んだこと' },
        ]}
      />

      {/* セクション1 */}
      <WorkflowSection title="この章で体験すること" id="section-1" number={1}>
        <WorkflowSection title="要件定義から実装への橋渡し" subNumber="1.1">
          <WorkflowText>
            要件定義編では「何を作るか」を決めてきました。ここからは「どう作るか」を決めるフェーズです。
          </WorkflowText>

          <WorkflowProcedureFlow
            steps={[
              {
                label: '要件定義フェーズ（完了）',
                sub: 'product.md / proposal.md / business.md / usecases.md',
                variant: 'external',
              },
              {
                label: '実装フェーズ（ここから）',
                sub: 'structure.md「どこに何を置くか」/ tech.md「どう書くか」',
              },
            ]}
          />

          <WorkflowText>
            「要件定義編 — Steeringルールの書き方」で「structure.md と tech.md
            は実装編で作る」と予告していました。この章がその伏線回収です。
          </WorkflowText>

          <WorkflowLinkCard
            title="要件定義編 — Steeringルールの書き方"
            description="structure/techの役割説明"
            path="/devdocs/handson/steering-rules"
          />
        </WorkflowSection>

        <WorkflowInfoBox type="info">
          <Typography variant="body2" fontWeight={600}>
            この章はPDCA生成ではありません。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            「要件定義編 — proposal/business生成」〜「要件定義編 —
            product作成」では「ルールを書いてAIに生成させ、チェックして改善する」サイクルでした。この章ではAIに生成物を作らせるのではなく、AIに渡すルールそのものを自分で考えて書きます。要件定義編のproduct.md（「要件定義編
            — Steeringルールの書き方」）と同じ「自分で書く」パターンです。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowLinkCard
          title="要件定義編 — proposal/business生成"
          description="PDCAサイクルの初回体験"
          path="/devdocs/handson/proposal-business"
        />
        <WorkflowLinkCard
          title="要件定義編 — Steeringルールの書き方"
          description="product.mdを自分で書いた記事"
          path="/devdocs/handson/steering-rules"
        />

        <WorkflowSection title="この章の流れ" subNumber="1.2">
          <WorkflowList
            variant="ordered"
            items={[
              'structure.md / tech.md の役割を再確認する',
              'TODOアプリの実装方針を決める（アーキテクチャ選定）',
              'structure.md を作成する',
              'tech.md を作成する',
              'チェックリストで品質を確認する',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="参照する記事" subNumber="1.3">
          <WorkflowText>
            以下のAIと作る、壊れないバックエンド紹介記事に、本プロジェクト（Kanzhong）で実際に使っているstructure.md
            /
            tech.mdの実例が掲載されています。規模感を参考にしてください（そのまま真似る必要はありません）。
          </WorkflowText>
          <WorkflowLinkCard
            title="バックエンド開発編 Part1 — スキーマ定義とAPI仕様書"
            description="スキーマ定義・API仕様書で利用するルール全文（structure/techが前提）"
            path="/devdocs/workflow/be-step1"
          />
          <WorkflowLinkCard
            title="バックエンド開発編 Part2 — API実装ワークフローの全体像"
            description="api_project_ruleがstructure/techを前提として動作する仕組み"
            path="/devdocs/workflow/be-step2"
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2 */}
      <WorkflowSection title="実装方針を決める" id="section-2" number={2}>
        <WorkflowSection title="まずアーキテクチャを選ぶ" subNumber="2.1">
          <WorkflowText>
            structure.md
            を書く前に、アーキテクチャ方針を決める必要があります。TODOアプリの規模であれば、以下のどちらかが現実的です。
          </WorkflowText>

          <WorkflowTable
            headers={['パターン', '特徴', '向いているケース']}
            rows={[
              [
                'レイヤードアーキテクチャ',
                'Controller → Service → Repository の3層。シンプルで学習コストが低い',
                '小〜中規模。TODOアプリにはこちらが適切',
              ],
              [
                'ヘキサゴナルアーキテクチャ',
                'ポートとアダプターでドメインを隔離。テスタビリティが高い',
                '中〜大規模。ビジネスロジックが複雑な場合',
              ],
            ]}
          />

          <WorkflowTipBox title="紹介記事のプロジェクトとの違い">
            <WorkflowText>
              紹介記事（Kanzhong）はヘキサゴナルアーキテクチャを採用しています。これは業務管理システムとして複雑なビジネスロジック（契約管理、申請承認フロー等）を扱うためです。
            </WorkflowText>
            <WorkflowText>
              TODOアプリは機能が限定的で、CRUDが中心です。ヘキサゴナルを採用すると、ポート・アダプターの分離による恩恵よりもボイラープレートのコストが上回ります。
            </WorkflowText>
            <WorkflowText>
              このハンズオンではレイヤードアーキテクチャを推奨します。ただし、ヘキサゴナルを選んでも構いません。その場合は紹介記事のstructure.mdを参考に、ポート/アダプターの配置ルールを追加してください。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="決めるべきこと" subNumber="2.2">
          <WorkflowText>structure.md / tech.md を書くために、以下を先に決めます。</WorkflowText>

          <WorkflowTable
            headers={['#', '決めること', '判断基準']}
            rows={[
              ['1', 'アーキテクチャパターン', '規模と複雑さで判断。TODOならレイヤード'],
              ['2', 'ベースパッケージ', 'Spring Initializrで設定した groupId + artifactId'],
              ['3', 'パッケージ分割方針', '機能別 or レイヤー別'],
              ['4', 'ORM の使い方', 'MyBatisならMapper, アノテーションSQL'],
              ['5', 'コーディング規約の範囲', '命名規則、Lombok使用ルール、禁止事項'],
            ]}
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              正解は1つではありません。大事なのは「ルールとして明文化し、プロジェクト内で一貫させる」ことです。ルールがないと、AIは毎回異なるスタイルのコードを生成してしまいます。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3 */}
      <WorkflowSection title="structure.mdを作成する" id="section-3" number={3}>
        <WorkflowSection title="書くべき情報（おさらい）" subNumber="3.1">
          <WorkflowText>
            「要件定義編 — Steeringルールの書き方」で紹介した雛形を思い出してください。structure.md
            に書くべき情報は:
          </WorkflowText>
          <WorkflowTable
            headers={['情報', '内容']}
            rows={[
              ['ディレクトリ構成', 'プロジェクトルートからのツリー図'],
              ['パッケージ構成', 'ベースパッケージと各サブパッケージの責務'],
              ['クラス命名規則', 'Controller, Service, Repository等の命名パターン'],
              ['レイヤー間の依存方向', '許可される依存/禁止される依存'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="レイヤードアーキテクチャの場合のパッケージ構成例" subNumber="3.2">
          <WorkflowText>
            レイヤードアーキテクチャを選んだ場合の典型的なパッケージ構成です。自分のプロジェクトに合わせてカスタマイズしてください。
          </WorkflowText>
          <WorkflowDirectoryTree
            root="com.example.teamtodo"
            entries={[
              {
                name: 'controller',
                desc: 'HTTPリクエストの受付',
                children: [{ name: 'dto', desc: 'Request/ResponseのDTO', children: [] }],
              },
              { name: 'service', desc: 'ビジネスロジック', children: [] },
              { name: 'repository', desc: 'DBアクセス（MyBatis Mapper）', children: [] },
              { name: 'entity', desc: 'DBテーブルと1対1のデータクラス', children: [] },
              {
                name: 'model',
                desc: 'ドメインモデル（Entityと異なるビジネスオブジェクト）',
                children: [],
              },
              { name: 'config', desc: 'Spring設定クラス', children: [] },
              { name: 'constant', desc: '定数Enum', children: [] },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="3.3">
          <WorkflowText>以下のファイルを作成してください。</WorkflowText>
          <WorkflowCodeBlock language="text" code=".kiro/steering/structure.md" />

          <WorkflowText>
            「要件定義編 —
            Steeringルールの書き方」の雛形をベースに、TODOアプリの構成に合わせて書いてください。
          </WorkflowText>

          <WorkflowLinkCard
            title="要件定義編 — Steeringルールの書き方"
            description="structure.mdの雛形"
            path="/devdocs/handson/steering-rules"
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              紹介記事（Kanzhong）のstructure.mdをそのままコピーしないでください。ヘキサゴナルアーキテクチャ前提のルール（ポート、アダプター、domain.repository等）はレイヤードアーキテクチャには不要です。
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              自分のアーキテクチャに合ったシンプルなルールを書いてください。パッケージ数は6〜8個で十分です。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="structure.md - 完成例" subNumber="3.4">
          <WorkflowLockedCodeBlock password="Kf7mWx">
            {`---
inclusion: always
---

# プロジェクト構造

## ルートディレクトリ

team-todo/
├── src/
│   └── main/
│       ├── java/com/example/teamtodo/
│       │   ├── controller/      # Web層
│       │   ├── service/         # ビジネスロジック層
│       │   ├── repository/      # データアクセス層
│       │   ├── entity/          # DBエンティティ
│       │   ├── model/           # ドメインモデル
│       │   ├── config/          # Spring設定
│       │   └── constant/        # 定数Enum
│       └── resources/
│           ├── application.yml
│           └── db/migration/    # Flywayマイグレーション
├── docs/                        # 設計書
│   ├── schema/                  # スキーマ定義
│   └── api/                     # API仕様書
├── docker-compose.yml
├── build.gradle
└── settings.gradle

## パッケージ構成

ベースパッケージ: com.example.teamtodo

| パッケージ | 役割 | 説明 |
|---|---|---|
| controller | Web層 | HTTPリクエストの受付とレスポンス返却 |
| controller.dto | DTO | リクエスト/レスポンスのデータ転送オブジェクト |
| service | ビジネスロジック層 | 業務ロジックの実装 |
| repository | データアクセス層 | MyBatis Mapperインターフェース |
| entity | エンティティ | DBテーブル行と1対1対応するデータクラス |
| model | ドメインモデル | ビジネスロジックで使用するオブジェクト |
| config | 設定 | Spring Security, MyBatis等の設定クラス |
| constant | 定数 | Enumによる定数定義 |

## クラス命名規則

| 種別 | 命名規則 | 例 |
|---|---|---|
| Controller | {リソース名}Controller | TodoController |
| Service | {リソース名}Service | TodoService |
| Repository (Mapper) | {リソース名}Mapper | TodoMapper |
| Entity | {リソース名}Entity | TodoEntity |
| Model | {リソース名} | Todo |
| RequestDto | {操作名}{リソース名}Request | CreateTodoRequest |
| ResponseDto | {操作名}{リソース名}Response | CreateTodoResponse |
| Enum | {定数名} | TodoStatus |

## レイヤー間の依存方向

[Controller] → [Service] → [Repository (Mapper)]
                              ↓
                           [Entity]

- Controller は Service のみに依存する。Repository を直接呼ばない。
- Service は Repository に依存する。Controller に依存しない。
- Repository（Mapper）は Entity を返却する。Model への変換は Service で行う。
- Entity は他のレイヤーに依存しない。
- Model は他のレイヤーに依存しない。

## データフロー

HTTP Request
  → Controller（リクエスト受付・バリデーション）
    → Service（ビジネスロジック・Model↔Entity変換）
      → Mapper（SQLアクセス）
        → DB`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4 */}
      <WorkflowSection title="tech.mdを作成する" id="section-4" number={4}>
        <WorkflowSection title="書くべき情報（おさらい）" subNumber="4.1">
          <WorkflowText>
            「要件定義編 — Steeringルールの書き方」で紹介した雛形を思い出してください。tech.md
            に書くべき情報は:
          </WorkflowText>
          <WorkflowTable
            headers={['情報', '内容']}
            rows={[
              ['技術スタック一覧', '言語・FW・DB・バージョン'],
              ['アーキテクチャ方針', '選定したパターンの概要'],
              ['コーディング規約', 'フォーマット、命名規則、Lombok使用ルール'],
              ['フレームワーク固有ルール', 'アノテーション、DI方式'],
              ['禁止事項', 'やってはいけないこと'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="どこまで細かく書くべきか" subNumber="4.2">
          <WorkflowTipBox title="ルールの粒度 — 最初から完璧を目指さない">
            <WorkflowText>
              tech.md
              は最初から全てのルールを網羅する必要はありません。実装を進める中で「AIがこういうコードを書いてきたけど、こうしてほしい」と感じたタイミングでルールを追加していけば十分です。
            </WorkflowText>
            <WorkflowText>最初に書いておくべき最低限:</WorkflowText>
            <WorkflowList
              items={[
                '技術スタック（バージョン含む）',
                'フォーマット基本ルール（インデント、文字コード）',
                'Lombokの使い方（使う/使わない、どのアノテーションを使うか）',
                'DI方式（コンストラクタインジェクション or フィールドインジェクション）',
              ]}
            />
            <WorkflowText>実装を進める中で追加すべき例:</WorkflowText>
            <WorkflowList
              items={[
                '「StringのnullチェックにはStringUtilsを使う」（AIが毎回 == null || isEmpty() で書いてきた）',
                '「recordは使わない」（AIがrecordを使い始めた）',
                '「Mapperではアノテーション(@Select等)でSQLを書く」（XMLを生成してきた）',
              ]}
            />
            <WorkflowText>
              ルールは「AIの出力に不満を感じたとき」に追加するのが最も効率的です。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="4.3">
          <WorkflowText>以下のファイルを作成してください。</WorkflowText>
          <WorkflowCodeBlock language="text" code=".kiro/steering/tech.md" />

          <WorkflowText>
            「要件定義編 —
            Steeringルールの書き方」の雛形をベースに、TODOアプリのコーディング規約を書いてください。
          </WorkflowText>

          <WorkflowLinkCard
            title="要件定義編 — Steeringルールの書き方"
            description="tech.mdの雛形"
            path="/devdocs/handson/steering-rules"
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              紹介記事のtech.mdは非常に詳細（60以上のルール）です。これは開発や運用を通じてルールが蓄積された結果です。最初から同じ量を書く必要はありません。
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              まず10〜15個のルールで十分です。実装編を進める中で「こうしてほしい」と感じたルールを随時追加してください。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="tech.md - 完成例" subNumber="4.4">
          <WorkflowLockedCodeBlock password="Qv3nLp">
            {`---
inclusion: always
---

# 技術スタック・コーディング規約

## 技術スタック

| カテゴリ | 技術 | バージョン |
|---|---|---|
| 言語 | Java | 21 |
| フレームワーク | Spring Boot | 4.0.x |
| ビルドツール | Gradle | 8.x |
| DB | MySQL | 8.0 |
| ORM | MyBatis Spring Boot Starter | 3.0.x |
| バリデーション | Spring Validation | - |
| コード生成 | Lombok | - |
| マイグレーション | Flyway | - |

## アーキテクチャ

レイヤードアーキテクチャ（3層）を採用する。

Controller（Web層）→ Service（ビジネスロジック層）→ Mapper（データアクセス層）

- 各レイヤーの責務を厳密に分離する。
- 依存の方向は上位層から下位層への一方向のみ。

## コーディング規約

### フォーマット

- インデント: スペース4つ
- 最大行長: 120文字
- 文字コード: UTF-8
- 改行コード: LF

### 命名規則

| 対象 | ルール | 例 |
|------|--------|-----|
| クラス | UpperCamelCase | TodoService |
| メソッド | lowerCamelCase、動詞で始める | createTodo, findById |
| 変数 | lowerCamelCase | todoName, assigneeId |
| 定数(Enum値) | UPPER_SNAKE_CASE | NOT_STARTED, IN_PROGRESS |
| パッケージ | 全て小文字 | controller, service |
| テーブル物理名 | snake_case | todo, todo_assignee |
| カラム物理名 | snake_case | todo_name, created_at |

### Lombok使用ルール

| アノテーション | 用途 |
|---|---|
| @Data | Entity, Model に使用（getter/setter/toString/equals/hashCode生成） |
| @RequiredArgsConstructor | DI対象クラス（Controller, Service）に使用 |
| @Builder | 必要に応じてEntity/Modelに使用 |

- フィールドインジェクション(@Autowired)は使用しない。コンストラクタインジェクションを使用する。

### DI・アノテーション

| クラス種別 | アノテーション |
|---|---|
| Controller | @RestController |
| Service | @Service |
| Mapper | @Mapper |
| 設定 | @Configuration |

- @Transactional は Service クラスの更新系メソッドに付与する。

### MyBatis

- SQLはアノテーション（@Select, @Insert, @Update, @Delete）で記述する。
- XMLマッピングは使用しない。
- 動的SQLが必要な場合は @SelectProvider 等を利用する。
- パラメータが複数の場合は @Param で名前を指定する。

### 定数

- 定数はEnumで定義する。static final は原則使用しない。
- Enum値には code（String）フィールドを持たせる。
- code値からEnumに変換する of(String code) staticメソッドを必ず作成する。

### バリデーション

- リクエストDTOに @Valid を付与する。
- 必須チェックは @NotNull（数値・オブジェクト）、@NotEmpty（String）を使用する。
- 桁数制限は @Size(max=N) を使用する。

### レスポンス

- リスト型のレスポンスはnullを返却しない。0件でも空リストを返す。
- 1件取得APIのレスポンスは result (object) でラップする。
- 複数件取得APIのレスポンスは results (object[]) でラップする。

## 禁止事項

- record は使用しない。
- ワイルドカードインポート（*）は使用しない。
- System.out.println は使用しない。ログは SLF4J を使用する。
- null を返すメソッドは極力避ける。コレクションは空コレクションを返す。
- Controllerで直接Mapperを呼び出さない（必ずServiceを経由する）。`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5 */}
      <WorkflowSection title="チェックリストで品質を確認する" id="section-5" number={5}>
        <WorkflowSection title="structure.md チェックリスト" subNumber="5.1">
          <WorkflowChecklist
            groups={[
              {
                title: 'structure.md チェックリスト',
                items: [
                  'inclusion: always が設定されているか',
                  'ディレクトリ構成がツリー図で記載されているか',
                  'ベースパッケージ名が記載されているか',
                  '全パッケージの役割と説明が記載されているか',
                  'クラス命名規則が種別ごとに記載されているか',
                  'レイヤー間の依存方向が明記されているか（何が何に依存してよいか/してはいけないか）',
                  '「導入編 — ストーリー導入 + プロジェクト雛形作成」で作成したプロジェクト構造（build.gradle, docker-compose.yml等）と矛盾していないか',
                  'product.md の機能（TODO管理）を実現するのに必要なパッケージが揃っているか',
                ],
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="tech.md チェックリスト" subNumber="5.2">
          <WorkflowChecklist
            groups={[
              {
                title: 'tech.md チェックリスト',
                items: [
                  'inclusion: always が設定されているか',
                  '技術スタック一覧に言語・FW・DB・ORMのバージョンが記載されているか',
                  '「導入編 — ストーリー導入 + プロジェクト雛形作成」のSpring Initializr設定（Java 21, Spring Boot 4.0.x, Gradle）と一致しているか',
                  'アーキテクチャ方針が明記されているか',
                  'フォーマット基本ルール（インデント、文字コード）が記載されているか',
                  '命名規則がクラス/メソッド/変数/定数で定義されているか',
                  'Lombokの使い方が明記されているか',
                  'DI方式（コンストラクタインジェクション）が明記されているか',
                  'MyBatisの使い方（アノテーションSQL or XML）が明記されているか',
                  '禁止事項が1つ以上記載されているか',
                ],
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="チェック不合格時の対応" subNumber="5.3">
          <WorkflowText>
            要件定義編と異なり、この章には「ルール改善→再生成」のサイクルはありません。自分が書いたルールの不備なので、自分で修正してください。
          </WorkflowText>
          <WorkflowText>
            ただし、ルールの品質は後続ステップで分かります。「実装編 — スキーマ定義 +
            API仕様書の生成」以降でAIがコードを生成したとき「パッケージの配置がおかしい」「命名規則が違う」と感じたら、structure.md
            / tech.md に戻ってルールを追記してください。
          </WorkflowText>
          <WorkflowLinkCard
            title="実装編 — スキーマ定義 + API仕様書の生成"
            description="次の記事"
            path="/devdocs/handson/schema-api"
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-6" number={6}>
        <WorkflowDirectoryTree
          root="team-todo/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'requirements',
                  children: [
                    {
                      name: 'todo',
                      children: [{ name: 'todo.rfp.md' }, { name: 'todo.proposal.md' }],
                    },
                    {
                      name: 'usecases',
                      children: [{ name: 'todo.business.md' }, { name: 'todo.usecases.md' }],
                    },
                  ],
                },
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md' },
                    { name: 'create_proposal.md' },
                    { name: 'create_business.md' },
                    { name: 'create_usecases.md' },
                    { name: 'create_product.md' },
                    { name: 'structure.md', desc: '今回作成', highlight: '#e65100' },
                    { name: 'tech.md', desc: '今回作成', highlight: '#e65100' },
                  ],
                },
              ],
            },
            { name: 'docs', desc: '設計書（次の記事で使用）' },
            { name: 'build.gradle' },
            { name: 'settings.gradle' },
            { name: 'docker-compose.yml' },
            { name: 'HELPME.md' },
            {
              name: 'src',
              children: [
                {
                  name: 'main',
                  children: [
                    {
                      name: 'java/com/example/teamtodo',
                      children: [
                        { name: 'TeamTodoApplication.java' },
                        {
                          name: 'config',
                          children: [{ name: 'SecurityConfig.java' }],
                        },
                      ],
                    },
                    {
                      name: 'resources',
                      children: [{ name: 'application.yml' }],
                    },
                  ],
                },
                { name: 'test/' },
              ],
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション7 */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-7" number={7}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: '最初はシンプルに、実装しながら育てる',
              effect: '全ルールを予測して書くのは不可能。AIの出力を見てルールを追加する方が効率的',
            },
            {
              practice: 'structure.md とtech.md の責務を分ける',
              effect:
                '「どこに置くか」と「どう書くか」を分離することで、変更時の影響範囲が明確になる',
            },
            {
              practice: '具体例を1つ添える',
              effect:
                '「UpperCamelCase」だけでなく「例: TodoService」と書くことでAIの解釈ブレを防ぐ',
            },
            {
              practice: '禁止事項を明記する',
              effect:
                '許可ルールだけでは防げないAIの挙動（record使用、XML生成等）を禁止事項で封じる',
            },
            {
              practice: 'inclusion: always にする',
              effect: 'structure/tech はコード生成の全場面で必要。fileMatchでは漏れる',
            },
            {
              practice: 'プロジェクト雛形と矛盾させない',
              effect:
                '「導入編 — ストーリー導入 + プロジェクト雛形作成」で決めた技術選定（Java 21, Gradle等）とtech.mdの記載を一致させる',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: '紹介記事のstructure/techをそのままコピーする',
              effect: 'アーキテクチャが異なるためルールとコードが矛盾する',
            },
            {
              practice: 'ルールを書かずに実装を始める',
              effect: 'AIが毎回異なるスタイルでコードを生成し、統一感のないコードベースになる',
            },
            {
              practice: '全クラスの一覧をstructure.mdに書く',
              effect:
                'クラスが増えるたびにメンテが必要。すぐ陳腐化する。命名規則で十分。具体例は1〜2個でよい',
            },
            {
              practice: '言語の基本文法をtech.mdに書く',
              effect:
                'コンテキスト枠の無駄遣い。AIは既に知っている。プロジェクト固有のルールのみ書く',
            },
            {
              practice: '最初から完璧なルール（50個以上）を目指す',
              effect: '書くことが目的になり実装が始められない。10〜15個で始めて、実装中に追加する',
            },
            {
              practice: 'product.mdにコーディングルールを書く',
              effect:
                '責務の混在。product.mdは「何を作るか」の責務。コーディングルールはtech.mdに分離する',
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション8 */}
      <WorkflowSection title="この章で学んだこと" id="section-8" number={8}>
        <WorkflowTable
          headers={['学び', '内容']}
          rows={[
            ['ルールは実装の土台', 'structure/techがないとAIは毎回異なる構造・スタイルで生成する'],
            [
              'シンプルに始めて育てる',
              '最初から完璧を目指さず、AIの出力を見ながらルールを追加する',
            ],
            ['3ファイルの責務分離', 'product=何を作るか、structure=どこに置くか、tech=どう書くか'],
            [
              'ルールの効果は後続ステップで分かる',
              '次の記事以降でAIが生成するコードの品質で効果を実感する',
            ],
          ]}
        />

        <WorkflowInfoBox type="preview">
          <Typography variant="body2" fontWeight={600}>
            次の記事: 実装編 — スキーマ定義 + API仕様書の生成
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            次の記事では以下を行います:
          </Typography>
          <WorkflowList
            items={[
              'スキーマ定義・API仕様書の生成ルール（create_schema.md / api_specs_rule.md）を作成する',
              'usecases.md + proposal.md を入力としてスキーマ定義・API仕様書を生成する',
              'PDCAサイクルでルールを改善しながら品質を上げる',
            ]}
          />
        </WorkflowInfoBox>
      </WorkflowSection>

      <WorkflowLinkCard
        title="実装編 — スキーマ定義 + API仕様書の生成"
        description="スキーマ定義・API仕様書の生成ルールを作成し、PDCAサイクルで品質を上げる"
        path="/devdocs/handson/schema-api"
      />

      <WorkflowChecklist
        groups={[
          {
            title: '最終チェックリスト',
            items: [
              '.kiro/steering/structure.md が作成されている',
              '.kiro/steering/tech.md が作成されている',
              '両ファイルとも inclusion: always で設定されている',
              'セクション5のチェックリスト全項目に合格している',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonStructureTech;
